import { Head, Link } from '@inertiajs/react';
import { ArrowLeft } from 'lucide-react';
import { useMemo } from 'react';
import { EmailsCard } from '@/components/admin/emails-card';
import { Button } from '@/components/ui/button';
import type { AdminSubmissionsShow } from '@/types';
import { ArchiveSubmissionButton } from './components/ArchiveSubmissionButton';
import { DraftInvitations } from './components/DraftInvitations';
import { DuplicateHackersAlert } from './components/DuplicateHackersAlert';
import { ReviewListNav } from './components/ReviewListNav';
import { TeamFlightRequests } from './components/TeamFlightRequests';
import { TeamMembers } from './components/TeamMembers';
import { TeamNotes } from './components/TeamNotes';
import { TeamOverview } from './components/TeamOverview';
import { TeamReview } from './components/TeamReview';
import { TeamStatusHistory } from './components/TeamStatusHistory';
import { TeamSubmission } from './components/TeamSubmission';

export default function Show({
  submissionData,
  emails,
  duplicateHackers,
  reviewers,
  evaluations,
  inviteSlots,
  draftEligibility,
  title,
  eventSlug,
  returnTo,
  listNav,
  isAdminFull,
  currentUserId,
}: AdminSubmissionsShow) {
  const { submission, members } = submissionData;

  // The Next page handed the members card a Map keyed by hacker id; the wire carries
  // the same rows as a list, so the map is rebuilt here.
  const evaluationByHackerId = useMemo(
    () => new Map(evaluations.map((e) => [e.hackerId, e])),
    [evaluations],
  );

  const memberNames = members.map((m) => m.fullName).join(', ');
  const isDraft = submission.status === 'draft';

  return (
    <>
      <Head title={title} />
      <div className="container mx-auto space-y-6 py-8">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" asChild>
              <Link href={returnTo}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Review
              </Link>
            </Button>
            <div>
              <h1 className="font-bold text-3xl tracking-tight">
                Submission Details
              </h1>
              <p className="text-muted-foreground">{memberNames}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {listNav && <ReviewListNav nav={listNav} eventSlug={eventSlug} />}
            {isAdminFull && (
              <ArchiveSubmissionButton
                eventSlug={eventSlug}
                submissionId={submission.id}
                memberNames={memberNames}
              />
            )}
          </div>
        </div>

        <DuplicateHackersAlert duplicateHackers={duplicateHackers} />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-6">
            <TeamOverview
              submission={submission}
              reviewers={reviewers}
              canEditModality={isAdminFull}
              eventSlug={eventSlug}
            />
            <TeamMembers
              submissionId={submission.id}
              eventSlug={eventSlug}
              submissionStatus={submission.status}
              members={members}
              isAdminFull={isAdminFull}
              evaluations={evaluationByHackerId}
            />
            {isDraft && <DraftInvitations slots={inviteSlots} />}
          </div>

          <div className="space-y-6">
            <TeamReview
              submissionId={submission.id}
              eventSlug={eventSlug}
              reviews={submissionData.reviews}
              currentUserId={currentUserId ?? undefined}
              submissionStatus={submission.status}
              submissionCohort={submission.cohort}
              submissionCountry={submission.country}
              hasFlightRequests={submissionData.flightRequests.length > 0}
              canPassToReceived={isAdminFull}
              draftEligibility={draftEligibility}
            />
            <TeamNotes
              notes={submissionData.notes}
              submissionId={submission.id}
              eventSlug={eventSlug}
            />
            <TeamSubmission submission={submission} />
            <TeamStatusHistory history={submissionData.statusHistory} />
            <EmailsCard emails={emails} emptyMessage="No emails sent to team members" />
            <TeamFlightRequests
              flightRequests={submissionData.flightRequests}
              submissionId={submission.id}
              eventSlug={eventSlug}
            />
          </div>
        </div>
      </div>
    </>
  );
}
